import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { brandAssets } from "@/lib/brand/tokens";
import { getCanonicalUrl } from "@/config/site";
import { TALK_TO_GENIE_PHONE_TEL } from "@/data/marketing/homepage";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

// Brand gradient — the same blue→magenta the revamped homepage runs on.
const SPARK_BLUE = "#4E9CFF";
const MAGENTA_BEAM = "#FF4DB8";

// The pitch: turn a dead end into proof of how good the genie is.
const VALUE_PROPS = [
  {
    genieIcon: "chat-check" as const,
    title: "Up to 80% resolved",
    description: "Support questions answered end to end, no human needed.",
  },
  {
    genieIcon: "globe" as const,
    title: "Every channel, 24/7",
    description: "Chat, voice, email and SMS. Always on, never a queue.",
  },
  {
    genieIcon: "microphone" as const,
    title: "Speaks your language",
    description: "Trained once on your business. On-brand every time.",
  },
  {
    genieIcon: "genie-lamp" as const,
    title: "Live within the hour",
    description: "Set it up today, answering customers before lunch.",
  },
];

// Quick escape hatches, styled as glass chips.
const QUICK_LINKS = [
  { label: "Home", to: "/" },
  { label: "How it works", to: "/how-it-works" },
  { label: "Talk to a live genie", to: "/live-agent/business-onboarding" },
  { label: "Contact", to: "/contact" },
];

// Decorative twinkle dots (not icons — pure glow), scattered behind the content.
const SPARKLES = [
  { top: "14%", left: "12%", size: 6, delay: "0s" },
  { top: "24%", left: "82%", size: 4, delay: "0.6s" },
  { top: "62%", left: "8%", size: 5, delay: "1.2s" },
  { top: "72%", left: "88%", size: 7, delay: "0.3s" },
  { top: "40%", left: "50%", size: 3, delay: "1.8s" },
  { top: "82%", left: "40%", size: 4, delay: "0.9s" },
];

const NotFound = () => {
  // Cursor-following spotlight for the immersive feel.
  const [glow, setGlow] = useState({ x: 50, y: 36 });
  const frame = useRef<number | null>(null);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;

    const onMove = (e: MouseEvent) => {
      const cx = e.clientX;
      const cy = e.clientY;
      if (frame.current) return;
      frame.current = requestAnimationFrame(() => {
        setGlow({
          x: (cx / window.innerWidth) * 100,
          y: (cy / window.innerHeight) * 100,
        });
        frame.current = null;
      });
    };

    window.addEventListener("mousemove", onMove);
    return () => {
      window.removeEventListener("mousemove", onMove);
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Page Not Found | Help Genie</title>
        <meta
          name="description"
          content="This page wandered off, but Help Genie is still here, resolving up to 80% of support questions on its own, 24/7, in your brand voice."
        />
        <meta name="robots" content="noindex,nofollow" />
        <link rel="canonical" href={getCanonicalUrl("/404")} />
      </Helmet>

      <main className="relative min-h-screen w-full overflow-hidden bg-[#0A0E1A] px-4 py-16 text-white sm:px-6">
        {/* Ambient brand auras — the same breathing glow as the homepage hero. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0">
          <div className="absolute -top-32 left-1/2 h-[42rem] w-[42rem] -translate-x-1/2 rounded-full bg-[#4E9CFF]/20 blur-[130px] animate-breathe motion-reduce:animate-none" />
          <div className="absolute -bottom-40 -left-24 h-[32rem] w-[32rem] rounded-full bg-[#FF4DB8]/16 blur-[120px] animate-breathe motion-reduce:animate-none [animation-delay:1.5s]" />
          <div className="absolute -bottom-24 right-0 h-[28rem] w-[28rem] rounded-full bg-[#4E9CFF]/12 blur-[120px] animate-breathe motion-reduce:animate-none [animation-delay:3s]" />
        </div>

        {/* Cursor-following spotlight. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-0"
          style={{
            background: `radial-gradient(600px circle at ${glow.x}% ${glow.y}%, rgba(78,156,255,0.16), transparent 42%)`,
          }}
        />

        {/* Drifting sparkles. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0">
          {SPARKLES.map((s, i) => (
            <span
              key={i}
              className="absolute rounded-full bg-white animate-twinkle motion-reduce:animate-none"
              style={{
                top: s.top,
                left: s.left,
                width: s.size,
                height: s.size,
                animationDelay: s.delay,
                boxShadow: "0 0 12px 2px rgba(255,255,255,0.5)",
              }}
            />
          ))}
        </div>

        <div className="relative z-10 mx-auto flex max-w-4xl flex-col items-center text-center">
          {/* The genie, floating out of a glowing portal — the focal point. */}
          <div className="relative mb-6 flex h-48 w-48 items-center justify-center sm:h-56 sm:w-56">
            {/* Portal glow + pulse ring, the beloved orb reimagined as a halo behind her. */}
            <span
              aria-hidden="true"
              className="absolute left-1/2 top-1/2 h-32 w-32 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-[#4E9CFF] to-[#FF4DB8] opacity-45 blur-2xl animate-breathe motion-reduce:animate-none sm:h-36 sm:w-36"
            />
            <span
              aria-hidden="true"
              className="absolute left-1/2 top-1/2 h-28 w-28 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/15 animate-pulse-ring motion-reduce:animate-none sm:h-32 sm:w-32"
            />
            {/* The mascot herself — real brand asset, floating and un-clipped. */}
            <img
              src={brandAssets.mascotLarge}
              alt="Help Genie mascot"
              className="relative z-10 h-full w-auto animate-float drop-shadow-[0_22px_45px_rgba(78,156,255,0.5)] motion-reduce:animate-none"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          </div>

          {/* Error eyebrow. */}
          <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-white/70 backdrop-blur-md">
            <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]" />
            Error 404 · Page not found
          </span>

          {/* Headline — keeps the beloved line, now with a shimmer. */}
          <h1 className="text-4xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
            This page{" "}
            <span
              className="animate-shimmer bg-clip-text text-transparent bg-[length:200%_100%]"
              style={{
                backgroundImage: `linear-gradient(110deg, ${SPARK_BLUE} 0%, ${MAGENTA_BEAM} 45%, ${SPARK_BLUE} 90%)`,
              }}
            >
              vanished into thin air
            </span>
            .
          </h1>

          {/* Subhead that sells. */}
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
            Good thing finding answers is what we do best. This page slipped
            away, but Help Genie is busy resolving up to 80% of support questions
            on its own, around the clock, in your brand's voice.
          </p>

          {/* Value props — proof of how good the genie is. */}
          <div className="mt-12 grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {VALUE_PROPS.map((prop) => (
              <div
                key={prop.title}
                className="group relative rounded-[20px] bg-gradient-to-br from-[#4E9CFF]/40 via-white/5 to-[#FF4DB8]/40 p-[1.5px] transition-all duration-500 hover:from-[#4E9CFF] hover:to-[#FF4DB8] hover:shadow-[0_25px_70px_-30px_rgba(78,156,255,0.55)]"
              >
                <div className="flex h-full flex-col items-center rounded-[19px] bg-[#0F1526] p-6 text-center transition-transform duration-500 ease-out group-hover:-translate-y-1">
                  <div
                    className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl"
                    style={{
                      background: `linear-gradient(135deg, ${SPARK_BLUE}22, ${MAGENTA_BEAM}22)`,
                    }}
                  >
                    <GenieIcon
                      name={prop.genieIcon}
                      size={30}
                      color={SPARK_BLUE}
                      secondaryColor={MAGENTA_BEAM}
                      gradientEnabled={true}
                      gradientAngle={135}
                      label={prop.title}
                    />
                  </div>
                  <h3 className="text-base font-semibold text-white">
                    {prop.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-white/60">
                    {prop.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Primary CTAs — same buttons as the homepage. */}
          <div className="mt-12 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
            <Link
              to="/signup"
              className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-xl px-8 text-base font-semibold text-white shadow-lg transition-all hover:-translate-y-0.5 hover:opacity-95"
              style={{
                background: `linear-gradient(90deg, ${SPARK_BLUE} 0%, ${MAGENTA_BEAM} 100%)`,
              }}
            >
              Start free
              <ArrowRight className="h-5 w-5" />
            </Link>
            <a
              href={`tel:${TALK_TO_GENIE_PHONE_TEL}`}
              className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-xl border-2 border-white/40 bg-white/5 px-7 text-base font-semibold text-white backdrop-blur-sm transition-all hover:border-white/60 hover:bg-white/10"
            >
              <Phone className="h-5 w-5" />
              Talk to a live genie
            </a>
          </div>

          {/* Quick nav chips. */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-2.5">
            {QUICK_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-white/70 backdrop-blur-md transition-all hover:border-white/25 hover:bg-white/10 hover:text-white"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </main>
    </>
  );
};

export default NotFound;
